import { TableDrawer } from "@components/TableDrawer/TableDrawer";
import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get } from "lodash";
import { useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import ConfirmationModal from "~/Components/Modal/confirmation-modal";
import { useNotification } from "~/Hooks/useNotification";
import { corporateCMSApi, post } from "~/Services";
import { corporateHost } from "~/Utils";
import { renderActionsColumn, renderDateColumn, renderPublishedColumn } from "~/Utils/TableRenderFunction";
import { drilldownDataCorporate } from "~/Utils/drilldownDataDum";

import ActionDropdown, { TypeData } from "~/Utils/ActionDropdown";
import { decodeHTML } from "~/Utils/CommonUtils";
import { useChildMenus } from "../MainNavigation.hook";

export const ChildMenus = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);
  const [pageSize, setPageSize] = useState<number>(10);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);

  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [data, setData] = useState<any>([]);
  const [sortedData, setSortedData] = useState<any>([]);
  const currentPageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [orderByName, setOrderByName] = useState<string | undefined>();
  const [drilldownData, setDrilldownData] = useState<any>(null);
  const [selectedPublished, setSelectedPublished] = useState<any>("");
  const publishedData: TypeData[] = [
    {
      label: "Yes",
      value: true,
    },
    {
      label: "No",
      value: false,
    },
  ];

  useEffect(() => {
    const fetchDrilldownData = async () => {
      const data = await drilldownDataCorporate();
      setDrilldownData(data);
    };

    fetchDrilldownData();
  }, []);

  const { data: drilldown, error, isLoading: drilldownLoading, isError, refetch: refetchDocument } = useChildMenus(id);

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  useEffect(() => {
    if (searchValue) {
      const filteredData = data.filter((item: any) => {
        return decodeHTML(item.name_en).toLowerCase().includes(decodeHTML(searchValue).toLowerCase());
      });
      setSortedData(filteredData);
      setTotalRecords(filteredData.length);
      setCurrentPage(0);
    } else {
      setSortedData(data);
      setTotalRecords(data.length);
      setCurrentPage(0);
    }
  }, [data, searchValue]);
  useEffect(() => {
    let filteredData = [...data];
    if (selectedPublished !== "") {
      const isPublished = selectedPublished === true;
      filteredData = filteredData.filter((item: any) => item.published === isPublished);
    }

    setSortedData(filteredData);
    setTotalRecords(filteredData.length);
    setCurrentPage(0);
  }, [selectedPublished, data]);

  const getRecords = () => {
    setIsLoading(true);
    let url = `/api/get-child-menu/allfields`;
    corporateCMSApi
      .get(url)
      .then((res: any) => {
        if (res && res.data) {
          let envelopes = res.data;
          setTotalRecords(envelopes.length);
          setData(envelopes);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setData([]);
        setIsLoading(false);
      });
  };

  const columns: TableColumnsType = [
    {
      title: "Title",
      dataIndex: "name_en",
      key: "name_en",
      render: (text) => decodeHTML(text),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "name_en"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "name_en"
            ? "descend"
            : undefined,
    },
    {
      title: "Published",
      dataIndex: "published",
      key: "published",
      render: renderPublishedColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "published"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "published"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      fixed: "right",
      render: (value: any) => renderDateColumn(value),
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "created_at"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "created_at"
            ? "descend"
            : undefined,
    },
    {
      title: "Last Update",
      dataIndex: "updated_at",
      key: "updated_at",
      fixed: "right",
      render: (value: any, record: any) => {
        if (value && record.created_at && value === record.created_at) {
          return "-";
        }
        return renderDateColumn(value);
      },
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updated_at"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updated_at"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (record: any) =>
        renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/corporate/navigation/childMenus/add?editId=${record.id}`);
          },
          undefined,
          setDeleteModalVisible,
          "users"
        ),
    },
  ];

  useEffect(() => {
    getRecords();
  }, []);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";

    if (sortOrder === "asc") {
      setSortOrder("desc");
      order = "desc";
    } else {
      setSortOrder("asc");
      order = "asc";
    }

    setOrderByName(column.dataIndex);

    const tempData = [...sortedData];
    setSortedData(tempData.sort((a: any, b: any) => handleSort(a, b, order, column.dataIndex)));
    setCurrentPage(0);
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Navigation",
      subModule: "childMenus",
      type: "DEDICATED",
      application: "MAHB_CORPORATE",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAHB_CORPORATE",
          apiUrl: apiUrl,
        },
      ],
      moduleKey: "MAHB_CORPORATE_Navigation",
      payload: data,
      status: status,
    };

    post("/management/publish", payload)
      .then((response: any) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
        if (response.status === "OK" && status == "PENDING") {
          navigate("/dedicated/status-information?activeTab=pending");
        } else {
          navigate("/dedicated/status-information?activeTab=draft");
        }
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  async function sendDeleteChildRequest(reviewNotes: string) {
    try {
      await onSubmit(
        selectedRowData,
        `${corporateHost}/api/get-child-menu-items-taxonomy-data-list/${selectedRowData?.id}`,
        "DELETE",
        "DELETE",
        "PENDING",
        reviewNotes
      );
    } catch (err) {}
  }
  const handlePublishedChange = (value: any) => {
    setSelectedPublished(value);
    setCurrentPage(0);
  };

  return (
    <>
      <DataTable
        columns={columns.map((col) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        multiSelect={false}
        className="table-clickable"
        tableData={
          currentPageData
            ? currentPageData?.map((d: any) => {
                return {
                  ...d,
                  name: decodeHTML(d.name),
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={5}
        onSearchChange={(searchText: string) => {
          setSearchValue(searchText);
          setCurrentPage(0);
        }}
        hideExport={true}
        isServerSearchApi={false}
        searchPlaceholderText={"Search Title"}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
        additionalCTA={
          <div className="flex items-center space-x-2">
            <ActionDropdown
              value={selectedPublished}
              options={publishedData}
              onChange={handlePublishedChange}
              placeholder="Select Published"
              className="w-48"
            />
          </div>
        }
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage + 1}
          total={totalRecords}
          pageSize={pageSize}
          onChange={(curPage, newPageSize) => {
            setCurrentPage(curPage - 1);
            if (newPageSize !== pageSize) {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }
          }}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
          // onChange={(curPage) => {
          //   setCurrentPage(curPage - 1);
          // }}
        />
      </div>

      <ConfirmationModal
        type="delete"
        open={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onSubmit={sendDeleteChildRequest}
        defaultReviewNotes="Kindly review this Navigation"
      />
      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={_get(drilldownData, _get(drilldown, "subModule", "childMenus"))}
        data={drilldown}
      />
    </>
  );
};

function handleSort(a: any, b: any, order: string, sortBy: string) {
  if (sortBy === "field_faqs_question") {
    const numberA = extractNumber(a[sortBy]);
    const numberB = extractNumber(b[sortBy]);

    if (order === "desc") {
      return numberA < numberB ? 1 : -1;
    } else if (order === "asc") {
      return numberA > numberB ? 1 : -1;
    } else {
      return 0;
    }
  }
  if (sortBy === "name" || sortBy === "field_parent_category") {
    const valueA = a[sortBy] ? String(a[sortBy]).toLowerCase() : "";
    const valueB = b[sortBy] ? String(b[sortBy]).toLowerCase() : "";

    if (order === "desc") {
      return valueA < valueB ? 1 : -1;
    } else if (order === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return 0;
    }
  }

  if (order === "desc") {
    return a[sortBy] < b[sortBy] ? 1 : -1;
  } else if (order === "asc") {
    return a[sortBy] > b[sortBy] ? 1 : -1;
  } else {
    return 0;
  }
}

function extractNumber(question: string): number {
  const match = question.match(/Q(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}
